import { Params } from '@angular/router';
import dayjs from 'dayjs';
import { IimDateRangeValue } from '@iim-angular/components';
import { ListInvoiceStatus } from './ListInvoiceStatus';
import { IInvoiceListFilterCommand } from './IInvoiceListFilterCommand';

/**
 * Class representing filter criteria for invoice lists.
 * Handles filter state and conversion between route parameters and filter objects.
 */
export class ListInvoiceFilter {
    /**
     * Creates an empty filter with default values.
     * @returns A new ListInvoiceFilter instance with default values
     */
    public static EMPTY(): ListInvoiceFilter {
        return new ListInvoiceFilter({
            status: ListInvoiceStatus.ALL,
            createDateFrom: dayjs().subtract(1, 'year').startOf('day').format(),
        });
    }

    private static readonly DOCUMENT_DATE_FROM = 'documentDateFrom';
    private static readonly DOCUMENT_DATE_TO = 'documentDateTo';
    private static readonly CREATE_DATE_FROM = 'createDateFrom';
    private static readonly CREATE_DATE_TO = 'createDateTo';
    private static readonly GENERAL_SEARCH = 'generalSearch';
    private static readonly INVOICE_STATUS = 'status';

    private readonly _documentDateFrom?: string;
    private readonly _documentDateTo?: string;
    private readonly _createDateFrom?: string;
    private readonly _createDateTo?: string;

    /**
     * Gets the document date from filter value.
     * @returns The document date from value as a string or undefined
     */
    public get documentDateFrom(): string | undefined {
        return this._documentDateFrom;
    }

    /**
     * Gets the document date to filter value.
     * @returns The document date to value as a string or undefined
     */
    public get documentDateTo(): string | undefined {
        return this._documentDateTo;
    }

    /**
     * Gets the document date range as a date range value object.
     * @returns A copy of the document date range object
     */
    public get documentDateRange(): IimDateRangeValue {
        return {
            ...this._documentDateRange,
        };
    }

    /**
     * Gets the create date from filter value.
     * @returns The create date from value as a string or undefined
     */
    public get createDateFrom(): string | undefined {
        return this._createDateFrom;
    }

    /**
     * Gets the create date to filter value.
     * @returns The create date to value as a string or undefined
     */
    public get createDateTo(): string | undefined {
        return this._createDateTo;
    }

    /**
     * Gets the create date range as a date range value object.
     * @returns A copy of the create date range object
     */
    public get createDateRange(): IimDateRangeValue {
        return {
            ...this._createDateRange,
        };
    }

    /**
     * The general search text filter.
     */
    public readonly generalSearch?: string;
    
    /**
     * The invoice status filter.
     */
    public readonly status: ListInvoiceStatus;
    
    private readonly _documentDateRange: IimDateRangeValue;
    private readonly _createDateRange: IimDateRangeValue;
    
    /**
     * Creates a new filter instance with the specified filter criteria.
     * @param init Optional initialization object with filter values
     */
    constructor(
        init?: Partial<{
            documentDateFrom?: string;
            documentDateTo?: string;
            createDateFrom?: string;
            createDateTo?: string;
            generalSearch?: string;
            status: ListInvoiceStatus;
        }>,
    ) {
        this._documentDateFrom = init?.documentDateFrom;
        this._documentDateTo = init?.documentDateTo;
        this._createDateFrom = init?.createDateFrom;
        this._createDateTo = init?.createDateTo;
        this.generalSearch = init?.generalSearch;
        this.status = init?.status || ListInvoiceStatus.ALL;
        this._documentDateRange = new IimDateRangeValue(
            this._documentDateFrom ? dayjs(this._documentDateFrom) : null,
            this._documentDateTo ? dayjs(this._documentDateTo) : null,
        );
        this._createDateRange = new IimDateRangeValue(
            this._createDateFrom ? dayjs(this._createDateFrom) : null,
            this._createDateTo ? dayjs(this._createDateTo) : null,
        );
    }

    /**
     * Creates a new filter with the specified document date range.
     * @param value The new document date range
     * @returns A new ListInvoiceFilter instance
     */
    public withDocumentDateRange(value: IimDateRangeValue): ListInvoiceFilter {
        return new ListInvoiceFilter({
            ...this.toObject(),
            documentDateFrom: value.from ? value.from.format() : undefined,
            documentDateTo: value.to ? value.to.format() : undefined,
        });
    }

    /**
     * Creates a new filter with the document date filter reset.
     * @returns A new ListInvoiceFilter instance with document date filter reset
     */
    public withResetDocumentDateFilter(): ListInvoiceFilter {
        return new ListInvoiceFilter({
            ...this.toObject(),
            documentDateFrom: undefined,
            documentDateTo: undefined,
        });
    }

    /**
     * Creates a new filter with the specified create date range.
     * @param value The new create date range
     * @returns A new ListInvoiceFilter instance
     */
    public withCreateDateRange(value: IimDateRangeValue): ListInvoiceFilter {
        return new ListInvoiceFilter({
            ...this.toObject(),
            createDateFrom: value.from ? value.from.format() : undefined,
            createDateTo: value.to ? value.to.format() : undefined,
        });
    }

    /**
     * Creates a new filter with the create date filter reset.
     * @returns A new ListInvoiceFilter instance with create date filter reset
     */
    public withResetCreateDateFilter(): ListInvoiceFilter {
        return new ListInvoiceFilter({
            ...this.toObject(),
            createDateFrom: undefined,
            createDateTo: undefined,
        });
    }

    /**
     * Creates a new filter with the specified general search text.
     * @param value The new general search text
     * @returns A new ListInvoiceFilter instance
     */
    public withGeneralSearch(value: string | undefined): ListInvoiceFilter {
        return new ListInvoiceFilter({
            ...this.toObject(),
            generalSearch: value,
        });
    }

    /**
     * Creates a new filter with the specified invoice status.
     * @param value The new invoice status
     * @returns A new ListInvoiceFilter instance
     */
    public withStatus(value: ListInvoiceStatus): ListInvoiceFilter {
        return new ListInvoiceFilter({
            ...this.toObject(),
            status: value,
        });
    }

    /**
     * Converts the filter to a plain object.
     * @returns A plain object representation of the filter
     */
    private toObject(): {
        documentDateFrom?: string;
        documentDateTo?: string;
        createDateFrom?: string;
        createDateTo?: string;
        generalSearch?: string;
        status: ListInvoiceStatus;
    } {
        return {
            documentDateFrom: this.documentDateFrom,
            documentDateTo: this.documentDateTo,
            createDateFrom: this.createDateFrom,
            createDateTo: this.createDateTo,
            generalSearch: this.generalSearch,
            status: this.status,
        };
    }
    
    /**
     * Creates a filter from a DTO.
     * @param dto The DTO to create the filter from
     * @returns A new ListInvoiceFilter instance
     */
    public static fromDto(dto: IInvoiceListFilterCommand): ListInvoiceFilter {
        return new ListInvoiceFilter({
            documentDateFrom: dto.documentDateFrom,
            documentDateTo: dto.documentDateTo,
            createDateFrom: dto.createDateFrom,
            createDateTo: dto.createDateTo,
            generalSearch: dto.generalSearch,
            status: dto.status,
        });
    }

    /**
     * Converts the filter to a DTO.
     * @returns An IInvoiceListFilterCommand object
     */
    public toDto(): IInvoiceListFilterCommand {
        const dto: IInvoiceListFilterCommand = {};

        if (this.documentDateFrom) {
            dto.documentDateFrom = this.documentDateFrom;
        }
        if (this.documentDateTo) {
            dto.documentDateTo = this.documentDateTo;
        }
        if (this.createDateFrom) {
            dto.createDateFrom = this.createDateFrom;
        }
        if (this.createDateTo) {
            dto.createDateTo = this.createDateTo;
        }
        if (this.generalSearch) {
            dto.generalSearch = this.generalSearch;
        }

        dto.status = this.status;

        return dto;
    }
    
    /**
     * Creates a filter from route parameters.
     * @param params Route parameters containing filter information
     * @returns A new ListInvoiceFilter instance
     */
    public static fromRouteParams(params: Params): ListInvoiceFilter {
        return new ListInvoiceFilter({
            documentDateFrom: params[this.DOCUMENT_DATE_FROM],
            documentDateTo: params[this.DOCUMENT_DATE_TO],
            createDateFrom: params[this.CREATE_DATE_FROM],
            createDateTo: params[this.CREATE_DATE_TO],
            generalSearch: params[this.GENERAL_SEARCH],
            status: params[this.INVOICE_STATUS] as ListInvoiceStatus,
        });
    }

    /**
     * Checks if the route parameters contain any filter parameters.
     * @param params Route parameters to check
     * @returns True if any filter parameters are present, false otherwise
     */
    public static isFilterParam(params: Params): boolean {
        return (
            params[ListInvoiceFilter.DOCUMENT_DATE_FROM] !== undefined ||
            params[ListInvoiceFilter.DOCUMENT_DATE_TO] !== undefined ||
            params[ListInvoiceFilter.CREATE_DATE_FROM] !== undefined ||
            params[ListInvoiceFilter.CREATE_DATE_TO] !== undefined ||
            params[ListInvoiceFilter.GENERAL_SEARCH] !== undefined ||
            params[ListInvoiceFilter.INVOICE_STATUS] !== undefined
        );
    }

    /**
     * Converts the filter to route parameters.
     * @returns Route parameters object
     */
    public toRouteParams(): Params {
        const params: Params = {};

        if (this.documentDateFrom) {
            params[ListInvoiceFilter.DOCUMENT_DATE_FROM] = this.documentDateFrom;
        }
        if (this.documentDateTo) {
            params[ListInvoiceFilter.DOCUMENT_DATE_TO] = this.documentDateTo;
        }
        if (this.createDateFrom) {
            params[ListInvoiceFilter.CREATE_DATE_FROM] = this.createDateFrom;
        }
        if (this.createDateTo) {
            params[ListInvoiceFilter.CREATE_DATE_TO] = this.createDateTo;
        }
        if (this.generalSearch) {
            params[ListInvoiceFilter.GENERAL_SEARCH] = this.generalSearch;
        }

        params[ListInvoiceFilter.INVOICE_STATUS] = this.status;

        return params;
    }
}